import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { ArrowRightIcon } from '@heroicons/react/solid';
import { Typography } from 'antd';
import clsx from 'clsx';
import Image, { StaticImageData } from 'next/image';
import Link from 'next/link';
import React, { useState } from 'react';

import ContactForm from './ContactForm';
import ButtonDefault, { ButtonVariants } from '../shared/basic/button';
import { ShouldRender } from '../shared/basic/ShouldRender';
import { ContactCardsDataProps } from '../../utils/interfaces';
import {
  BookIcon,
  BusinessIcon,
  CallIcon,
  Career,
  LocationIcon,
  MessageQuestionIcon,
  Order,
  ReturnIcon,
  WhatsAppPngIcon,
} from '../../../assets';
import EmptyIconWrite from '../../../assets/Icons/EmptyIconWrite';

interface ISupportCard {
  imageUrl: StaticImageData;
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
}

const ContactCardsData = [
  {
    icon: ReturnIcon,
    label: 'Return & Refund',
    desc: 'Track your Return & Refund Orders',
    redirectionLink:
      'https://support.zebralearn.com/support/solutions/folders/1060000227328',
  },
  {
    icon: Order,
    label: 'Orders',
    desc: 'Check Order Status, Cancel Order & Discount Codes',
    redirectionLink:
      'https://support.zebralearn.com/support/solutions/folders/1060000227312',
  },
  {
    icon: BookIcon,
    label: 'Zebralearn books',
    desc: 'How to access e-books',
    redirectionLink:
      'https://support.zebralearn.com/support/solutions/folders/1060000227336',
  },
  {
    icon: MessageQuestionIcon,
    label: 'All FAQs',
    desc: 'Got a question? Get the answers here!',
    redirectionLink:
      'https://support.zebralearn.com/support/solutions/1060000029772',
  },
];

const SupportCard = ({
  imageUrl,
  title,
  description,
  buttonText,
  buttonLink,
}: ISupportCard) => {
  return (
    <div className='flex justify-center rounded-md bg-readerLightGrayBg py-7 mt-5'>
      <div className='flex gap-4 flex-col md:flex-row w-full px-4 md:px-8'>
        <div className='flex gap-4 items-center w-full'>
          <span className='flex items-center justify-between gap-x-4 lg:gap-x-10'>
            <p className='w-14 xs:w-16  md:w-14 mx-auto bg-black p-4 rounded-full flex justify-center items-center'>
              {title === 'Still need help?' ? (
                <ExclamationCircleIcon className='text-fadedWhite w-7' />
              ) : title === 'Write with us' ? (
                <EmptyIconWrite
                  width={24}
                  height={24}
                  fill='#FFFFFF'
                  stroke='#FFFFFF'
                />
              ) : (
                <Image
                  src={imageUrl}
                  alt='SupportImage'
                  className='object-contain w-full h-full'
                />
              )}
            </p>
          </span>

          <span className='flex flex-col lg:flex-row lg:gap-x-5 w-full px-5 items-center'>
            <p className='text-lg xs:text-xl  w-full lg:w-1/3 font-bold text-fadedWhite leading-6'>
              {title}
            </p>
            <span className='text-fadedWhite w-full lg:w-2/3 text-sm xs:text-base'>
              <p className='w-full xl:w-4/5 pt-2 lg:pt-0 lg:pl-5  font-light'>
                {description}{' '}
              </p>
            </span>
          </span>
        </div>

        <span className=' flex items-center justify-center w-full md:w-auto md:justify-end'>
          <ButtonDefault
            onClick={() => window?.open(buttonLink, '_blank')}
            size={4}
            roundedSm={true}
            variant={ButtonVariants.UNSTYLED}
            className={clsx(
              title === 'Raise query' ? 'border-primary' : 'border-[#363636]',
              'bg-black  border rounded-md text-fadedWhite w-full md:w-auto',
            )}
          >
            <span className='flex text-xs gap-x-2 px-4 py-1 whitespace-nowrap uppercase'>
              {buttonText}
            </span>
          </ButtonDefault>
        </span>
      </div>
    </div>
  );
};

const CardSection: React.FC<ContactCardsDataProps> = ({ data }) => {
  return (
    <>
      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 justify-between gap-2 xs:gap-3 md:gap-4 xl:gap-8 '>
        {data.map((item, index) => (
          <div
            key={`${item?.label}-${index}`}
            onClick={() => {
              if (window !== undefined && item.redirectionLink) {
                window.open(item.redirectionLink, '_blank');
              }
            }}
            className='flex flex-col gap-y-2 md:gap-y-5 cursor-pointer hover:bg-cartBgColor bg-readerLightGrayBg text-center px-4 md:px-8 pt-5 md:pt-10 pb-3 md:pb-5 rounded-md'
          >
            <div className='w-12 xs:w-16 mx-auto bg-black p-3 xs:p-4 rounded-full'>
              <Image
                src={item.icon}
                alt={item.label}
                className='object-contain w-full h-full'
              />
            </div>
            <div className='flex flex-col gap-y-2'>
              <span className='text-sm xs:text-base md:text-xl font-bold text-fadedWhite leading-6 xs:mt-2 md::mt-4'>
                {item.label}
              </span>
              <span className='text-fadedWhite text-xs xs:text-sm md:text-base font-light'>
                {item.desc}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const ContactPage = () => {
  const [visible, setVisible] = useState({ value: false, type: '' });
  const onCancel = () => {
    setVisible({ value: false, type: '' });
  };
  const raiseQueryData = [
    {
      imageUrl: BusinessIcon,
      title: 'Raise query',
      description:
        'Fill up the support ticket form and we will get back to you',
      buttonText: 'RAISE A TICKET',
      buttonLink: 'https://support.zebralearn.com/support/tickets/new',
    },
    // {
    //   imageUrl: WhatsAppPngIcon,
    //   title: 'Contact Us',
    //   description:
    //     'Hello Learner, feel free to chat with us if you encounter any issues',
    //   buttonText: 'WhatsApp',
    //   buttonLink: 'https://wa.me/917984460832',
    // },
  ];
  const contactUs = [
    {
      imageUrl: WhatsAppPngIcon,
      title: 'Write with us',
      description: 'Bring your idea on the table & Write a book with us',
      buttonText: 'Contact us',
      buttonLink: 'https://forms.gle/7KypUwLjqGg6Ei8t5',
    },
  ];
  const supportData = [
    {
      imageUrl: BusinessIcon,
      title: 'Business inquiries / partnerships',
      description: 'Want to collaborate with us? Fill up the form!',
      buttonText: 'Contact Us',
      buttonLink: 'https://forms.gle/GDL1VrQEDQXaPvKJA',
    },
    {
      imageUrl: MessageQuestionIcon,
      title: 'Feedback / Suggestions',
      description: 'Got something to say? Drop your Feedback here!',
      buttonText: 'Help Us Improve',
      buttonLink:
        'https://docs.google.com/forms/d/1JFTvAUL_5wGtd2oHMsBgIKzefBcbNUKTeW2Gt928DsA/edit',
    },
  ];
  return (
    <>
      <div className='relative px-2 xs:px-5 xl:px-10 overflow-hidden'>
        <div className='flex justify-center '>
          <Typography.Title level={1} className='hidden'>
            H1 tag
          </Typography.Title>
          <div className='flex flex-col gap-y-5  md:px-12 xl:px-0 xl:w-2/3 mt-5 md:mt-10 lg:mt-28'>
            <div>
              <Typography.Title
                level={2}
                className='text-xl md:text-2xl xs:mt-5 md:mt-10 lg:mt-0 font-medium text-start flex justify-start text-fadedWhite'
              >
                Facing any issue?
              </Typography.Title>
              {raiseQueryData.map((data, index) => (
                <SupportCard key={`${data?.title}-${index}`} {...data} />
              ))}
            </div>
            <div className='mt-5  md:mt-10'>
              <Typography.Title
                level={2}
                className='text-xl md:text-2xl xs:mt-5 md:mt-10 lg:mt-0 font-medium text-start flex justify-start text-fadedWhite'
              >
                Let’s Create a Book Together
              </Typography.Title>
              {contactUs.map((data, index) => (
                <SupportCard key={`${data?.title}-${index}`} {...data} />
              ))}
            </div>
            <div className='mt-5  md:mt-10'>
              <Typography.Title
                level={2}
                className='text-xl  md:text-2xl font-medium text-start pb-6 flex justify-start text-fadedWhite'
              >
                Frequently Asked Questions
              </Typography.Title>
              <div>
                <CardSection data={ContactCardsData} />
              </div>
            </div>

            <div>
              <span className='text-xl md:text-2xl xs:mt-5 md:mt-10 font-medium text-start flex justify-start text-fadedWhite'>
                Still need help ?
              </span>
              {supportData.map((data, index) => (
                <SupportCard key={`${data?.title}-${index}`} {...data} />
              ))}
            </div>

            <div className='flex gap-y-4 md:gap-y-8 flex-col'>
              <Typography.Title
                level={3}
                className='text-xl md:text-2xl mt-5 md:mt-10 font-medium text-start flex justify-start text-fadedWhite'
              >
                Other questions you might be interested in?
              </Typography.Title>
              <div className='bg-readerLightGrayBg rounded-md flex flex-col md:flex-row py-10 '>
                <span className='md:w-5/12 px-10 py-5 flex justify-end '>
                  <Image
                    src={Career}
                    alt='Career'
                    width={370}
                    height={265}
                    className='object-contain '
                  />
                </span>
                <span className='md:w-7/12 text-fadedWhite flex items-center px-5'>
                  <div className='flex flex-col gap-y-1 md:gap-y-3'>
                    <Typography.Title
                      level={2}
                      className='text-xl md:text-2xl lg:text-2xl text-textGrayColor'
                    >
                      Join ZebraLearn
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      className='text-xl md:text-2xl lg:text-4xl lg:w-2/3'
                    >
                      Join the Creative & Dynamic Team of ZebraLearn
                    </Typography.Title>
                    <span className='flex gap-5 mt-5'>
                      <ButtonDefault
                        size={2}
                        rounded={true}
                        variant={ButtonVariants.WHITE}
                        onClick={() => {
                          if (window !== undefined) {
                            window.open(
                              'https://www.linkedin.com/jobs/search/?currentJobId=3879790265&geoId=102713980&keywords=zebralearn&location=India&originalSubdomain=in',
                              '_blank',
                            );
                          }
                        }}
                        className='flex justify-center px-5   font-medium text-xs w-full lg:w-auto whitespace-nowrap'
                      >
                        <span className='px-2 text-sm font-semibold'>
                          Join us
                        </span>
                      </ButtonDefault>
                    </span>
                  </div>
                </span>
              </div>

              <div className='flex flex-col md:flex-row  w-full gap-y-8 pb-10 '>
                <div className=' flex flex-col lg:flex-row gap-5 md:gap-10 w-full'>
                  <div className='flex w-full flex-col gap-y-2 md:gap-y-4 text-fadedWhite rounded-md p-5 md:p-10 bg-readerLightGrayBg'>
                    <span className='w-16 md:w-20'>
                      <Image
                        src={CallIcon}
                        alt='Envelope'
                        className='object-contain w-full h-full'
                      />
                    </span>
                    <span className='text-base md:text-lg lg:text-xl text-textGrayColor    leading-6'>
                      Contact Us
                    </span>
                    <span className='text-xl md:text-2xl lg:text-3xl'>
                      Please reach out for any further queries
                    </span>
                    <ButtonDefault
                      onClick={() => window?.open('https://support.zebralearn.com/support/tickets/new', '_blank')}
                      size={4}
                      roundedSm={true}
                      variant={ButtonVariants.UNSTYLED}
                      className={clsx(
                        'border-white',
                        'bg-black  border rounded-md text-fadedWhite w-full md:w-auto mt-8',
                      )}
                    >
                      <span className='flex text-xs gap-x-2 px-4 py-1 whitespace-nowrap uppercase'>
                        RAISE A TICKET
                      </span>
                    </ButtonDefault>
                  </div>
                  <div className='flex w-full flex-col gap-y-2 md:gap-y-4 text-fadedWhite rounded-md p-5 md:p-10 bg-readerLightGrayBg'>
                    <span className='w-16 md:w-20'>
                      <Image
                        src={LocationIcon}
                        alt='Envelope'
                        className='object-contain w-full h-full'
                      />
                    </span>

                    <div className='flex col-span-2 flex-col gap-y-2 md:gap-y-4'>
                      <span className='text-base md:text-lg lg:text-xl text-textGrayColor    leading-6'>
                        Office Address
                      </span>
                      <span className='text-xl md:text-2xl lg:text-3xl'>
                        5th Floor, Kalp Business Center, City Light, Surat
                      </span>
                      <span className='text-lg md:text-xl font-semibold text-[#166FF6] mt-2'>
                        10 AM - 7 PM (Monday to Saturday)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <ShouldRender check={false}>
              <div className='flex gap-x-5 justify-center py-10'>
                <span>
                  <ButtonDefault size={4} variant={ButtonVariants.PRIMARY}>
                    <span className='flex text-xs gap-x-2 md:px-4 py-1 whitespace-nowrap'>
                      WRITE WITH US
                      <ArrowRightIcon className='w-4 h-4 text-white' />
                    </span>
                  </ButtonDefault>
                </span>
                <span>
                  <Link href='/contact/join'>
                    <ButtonDefault size={4} variant={ButtonVariants.PRIMARY}>
                      <span className='flex text-xs gap-x-2 md:px-4 py-1 whitespace-nowrap'>
                        JOIN ZEBRALEARN
                        <ArrowRightIcon className='w-4 h-4 text-white' />
                      </span>
                    </ButtonDefault>
                  </Link>
                </span>
              </div>
            </ShouldRender>
          </div>
        </div>
      </div>
      <ContactForm onCancel={onCancel} visible={visible} />
    </>
  );
};

export default ContactPage;
